import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, shareReplay, tap } from 'rxjs';
import { ZentraComponent } from '../interfaces/subProduct.interface';
import { SubProductsService } from './sub-products.service';
import { FormArray } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
/* istanbul ignore next */
export class SubComponentsService {
  componentOptionsSubject = new BehaviorSubject<ZentraComponent[]>([]);
  selectedComponentsSubject = new BehaviorSubject<
    { component: ZentraComponent; quantity: number }[]
  >([]);

  constructor(readonly productService: SubProductsService) {}

  fetchComponentOptionsGeneric(
    fetchFn: () => Observable<ZentraComponent[]>
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.componentOptionsSubject.getValue().length === 0) {
        fetchFn()
          .pipe(
            tap((components: ZentraComponent[]) => {
              console.log('Fetching component options from API');
              this.componentOptionsSubject.next(components); // Update the BehaviorSubject
            }),
            shareReplay(1)
          )
          .subscribe({
            next: () => {
              console.log('Component options fetched and cached');
              resolve();
            },
            error: (error) => {
              console.error('Error fetching component options:', error);
              reject(
                new Error(
                  `Error fetching component options: ${error.message ?? error}`
                )
              );
            },
          });
      } else {
        console.log('Component options already cached');
        resolve();
      }
    });
  }
  /* istanbul ignore next */
  fetchComponentOptions(): Promise<void> {
    return this.fetchComponentOptionsGeneric(() =>
      this.productService.getZentraComponents()
    );
  }
  /* istanbul ignore next */
  fetchComponentOptionsIntegrator(): Promise<void> {
    return this.fetchComponentOptionsGeneric(() =>
      this.productService.getZentraComponentsIntegrator()
    );
  }

  /* istanbul ignore next */
  fetchComponentOptionsGuestPurchaser(): Promise<void> {
    return this.fetchComponentOptionsGeneric(() =>
      this.productService.getZentraComponentsGuestPurchaser()
    );
  }

  /* istanbul ignore next */
  fetchSATComponentOptions(): Promise<void> {
    // Always force a fresh fetch for SAT — don't use Zentra cache
    this.componentOptionsSubject.next([]);
    return this.fetchComponentOptionsGeneric(() =>
      this.productService.getSATComponents()
    );
  }

  /* istanbul ignore next */
  getComponentOptions(): Observable<ZentraComponent[]> {
    return this.componentOptionsSubject.asObservable();
  }
  /* istanbul ignore next */
  updateSelectedComponents(
    components: FormArray,
    componentOptions: ZentraComponent[]
  ): void {
    console.log('updateSelectedComponents in service');
    const selectedComponents = components.controls
      .map((control) => {
        const componentId = control.get('componentId')?.value;
        const quantity = control.get('allocatedQuantity')?.value ?? 1;
        const pricePointId = control.get('pricePointId')?.value;
        const useCustomPrice = control.get('useCustomPrice')?.value;
        const customPrice = control.get('customPrice')?.value;
        const selectedComponent = componentOptions.find(
          (component) => component.id === componentId
        );

        if (selectedComponent) {
          return {
            component: selectedComponent,
            quantity,
            pricePointId,
            useCustomPrice,
            customPrice: useCustomPrice ? customPrice : null,
          };
        }
        return null;
      })
      .filter(
        (
          item
        ): item is {
          component: ZentraComponent;
          quantity: number;
          pricePointId: string;
          useCustomPrice: boolean;
          customPrice: number | null;
        } => item !== null
      );

    this.selectedComponentsSubject.next(selectedComponents);
  }
  /* istanbul ignore next */
  getSelectedComponents(): Observable<
    { component: ZentraComponent; quantity: number }[]
  > {
    return this.selectedComponentsSubject.asObservable();
  }
}
